// LoginCard — username + password (primary), passkey, TOTP, and backup code.
// Styling uses the design system classes from styles/auth.css.

"use client";

import { useRouter } from "@/i18n/navigation";
import { ApiClientError } from "@/lib/api";
import { PALETTE_LABELS } from "@/lib/palette";
import { usePaletteStore } from "@/lib/palette-store";
import { startAuthentication } from "@simplewebauthn/browser";
import { useTranslations } from "next-intl";
// useSearchParams stays on next/navigation — it's not locale-aware
// (just reads the query string), and i18n/navigation doesn't re-export it.
import { useSearchParams } from "next/navigation";
import {
  type ClipboardEventHandler,
  type KeyboardEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";

type ErrorKind =
  | "wrong-creds"
  | "wrong-totp"
  | "wrong-backup"
  | "no-passkey"
  | "no-password"
  | "cancelled"
  | "lockout"
  | "generic"
  | null;

interface ErrorMeta {
  title: string;
  body: string;
  cta?: { label: string; href: string };
}

/** Maps the local ``ErrorKind`` discriminator to a translation-catalog
 *  key under ``auth.errors.<key>``. Each catalog entry has ``.title`` +
 *  ``.body`` sub-keys. */
const ERROR_KIND_TO_KEY: Record<NonNullable<ErrorKind>, string> = {
  "wrong-creds": "wrongCreds",
  "wrong-totp": "wrongCode",
  "wrong-backup": "invalidBackup",
  "no-passkey": "noPasskey",
  "no-password": "noPassword",
  cancelled: "passkeyCancelled",
  lockout: "lockedOut",
  generic: "signInFailed",
};

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  if (!res.ok) {
    const text = await res.text();
    throw new ApiClientError(res.status, `HTTP_${res.status}`, text || res.statusText);
  }
  return (await res.json()) as T;
}

function classify(e: unknown, hint?: "totp" | "backup" | "password" | "passkey"): ErrorKind {
  if (e instanceof Error) {
    const msg = e.message.toLowerCase();
    if (msg.includes("not allowed") || msg.includes("cancelled") || msg.includes("abort")) {
      return "cancelled";
    }
    if (msg.includes("password not set")) return "no-password";
    if (msg.includes("no credentials") || msg.includes("not registered") || msg.includes("404")) {
      return "no-passkey";
    }
    if (msg.includes("lock") || msg.includes("429")) return "lockout";
    if (msg.includes("401") || msg.includes("invalid")) {
      if (hint === "totp") return "wrong-totp";
      if (hint === "backup") return "wrong-backup";
      if (hint === "password") return "wrong-creds";
    }
  }
  return "generic";
}

// 6-box OTP input.
function OtpDigits({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}): React.JSX.Element {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = value.padEnd(6, " ").slice(0, 6).split("");

  function setDigit(idx: number, ch: string) {
    const clean = ch.replace(/[^0-9]/g, "").slice(0, 1);
    const next = digits.map((d) => (d === " " ? "" : d));
    next[idx] = clean;
    onChange(next.join(""));
    if (clean && idx < 5) {
      refs.current[idx + 1]?.focus();
      refs.current[idx + 1]?.select();
    }
  }

  const onKey: KeyboardEventHandler<HTMLInputElement> = (e) => {
    const idx = Number((e.target as HTMLInputElement).dataset.idx);
    if (Number.isNaN(idx)) return;
    if (e.key === "Backspace" && !digits[idx]?.trim() && idx > 0) {
      e.preventDefault();
      const next = digits.map((d) => (d === " " ? "" : d));
      next[idx - 1] = "";
      onChange(next.join(""));
      refs.current[idx - 1]?.focus();
    } else if (e.key === "ArrowLeft" && idx > 0) {
      e.preventDefault();
      refs.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < 5) {
      e.preventDefault();
      refs.current[idx + 1]?.focus();
    }
  };

  const onPaste: ClipboardEventHandler<HTMLInputElement> = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    onChange(pasted.padEnd(6, "").slice(0, 6));
    const focusIdx = Math.min(pasted.length, 5);
    refs.current[focusIdx]?.focus();
  };

  return (
    <div className="digits" role="group" aria-label="6-digit one-time code">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          data-idx={i}
          data-testid={`otp-digit-${i}`}
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          aria-label={`Digit ${i + 1} of 6`}
          maxLength={1}
          className={digits[i]?.trim() ? "filled" : ""}
          value={digits[i]?.trim() ?? ""}
          onChange={(e) => setDigit(i, e.target.value)}
          onKeyDown={onKey}
          onPaste={onPaste}
        />
      ))}
    </div>
  );
}

type CodeMode = "totp" | "backup";

export function LoginCard() {
  const router = useRouter();
  const params = useSearchParams();
  const next = params.get("next") ?? "/";
  // Active palette drives the hanko-stamp glyph in the auth brand.
  const palette = usePaletteStore((s) => s.palette);
  const tErrors = useTranslations("auth.errors");
  const tLogin = useTranslations("auth.login");

  // Post-login navigation. We use a full-page assign instead of
  // ``router.push`` because Next.js's router cache may hold the
  // pre-login (unauthenticated) RSC payload for the destination
  // route — typically because middleware redirected ``/`` → ``/login``
  // before the cookies were set. Soft navigation would render that
  // stale empty payload and the user has to manually reload to see
  // their home page. A hard navigation guarantees the layout re-runs
  // with the freshly-set session + CSRF cookies.
  async function navigateAfterLogin() {
    // Confirm the session actually took before navigating. Prior code
    // navigated immediately on the login POST returning 200, but the
    // browser hadn't always committed the Set-Cookie header by the time
    // the assigned URL loaded — the destination request went WITHOUT
    // the session cookie, the layout RSC saw 401 and bounced back to
    // /login, and the operator saw "login didn't work; try again".
    // Polling whoami up to ~1 s confirms the cookie is live client-
    // side. The typical wait is 0-1 attempt; the retry budget only
    // triggers on unusually slow browser cookie commits.
    if (typeof window !== "undefined") {
      for (let i = 0; i < 5; i++) {
        try {
          const r = await fetch("/api/auth/whoami", {
            credentials: "include",
            cache: "no-store",
            headers: { Accept: "application/json" },
          });
          if (r.ok) break;
        } catch {
          /* keep trying */
        }
        await new Promise((r) => setTimeout(r, 200));
      }
    }
    // Notify any sibling tabs that are also sitting on /login (operator
    // opened multiple tabs, session expired everywhere, logs in on one).
    // The other tabs listen for this message + reload themselves into
    // the portal — no manual refresh required.
    //
    // Fan out across three channels — some browsers gate one or the
    // other under specific isolation policies (Cloudflare Tunnel + a
    // stale service worker was silently swallowing the BroadcastChannel
    // message in prod). Any single channel arriving is enough.
    if (typeof BroadcastChannel !== "undefined") {
      try {
        const bc = new BroadcastChannel("vc-auth");
        bc.postMessage({ type: "logged-in", at: Date.now() });
        bc.close();
      } catch {
        /* BroadcastChannel may be blocked in some embedded contexts */
      }
    }
    try {
      // localStorage change fires the ``storage`` event in every OTHER
      // tab on the same origin — the sending tab does not receive it,
      // which is exactly what we want here.
      window.localStorage.setItem(
        "vc-auth-signal",
        String(Date.now()),
      );
    } catch {
      /* localStorage may be disabled (private mode strictness) */
    }
    if (typeof window !== "undefined") {
      window.location.assign(next || "/");
    } else {
      router.push(next || "/");
    }
  }

  // Listener side of the cross-tab login signal. When THIS tab is the
  // one still sitting on /login (because the operator logged in on a
  // sibling tab), navigate into the portal automatically once the
  // shared session cookie lands in the browser jar.
  //
  // Four channels, any one is enough:
  //   1. BroadcastChannel — instant fanout, blocked in some sandboxes
  //   2. ``storage`` event — instant fanout, blocked when localStorage
  //      is denied
  //   3. ``visibilitychange`` — fires when the operator manually
  //      switches to this tab; free re-check
  //   4. 5s poll of ``/api/auth/whoami`` — the belt-and-braces fallback
  //      when both (1) and (2) get swallowed by a service worker or a
  //      strict site-isolation policy. Cheap: cached-cookie fast path
  //      on the API side + short-circuits the moment the session lands.
  //
  // The last channel is what makes the UX guarantee robust: once the
  // cookie is in the browser jar, sibling tabs will pick it up within
  // 5 s even in adversarial network conditions.
  useEffect(() => {
    let cancelled = false;
    const navigate = () => {
      if (cancelled) return;
      cancelled = true;
      if (typeof window !== "undefined") window.location.assign(next || "/");
    };
    const checkWhoami = async () => {
      try {
        const r = await fetch("/api/auth/whoami", {
          credentials: "include",
          headers: { Accept: "application/json" },
          cache: "no-store",
        });
        if (r.ok) navigate();
      } catch {
        /* offline / network blip — retry on the next tick */
      }
    };
    const onBroadcast = (e: MessageEvent) => {
      if (e.data?.type === "logged-in") navigate();
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === "vc-auth-signal" && e.newValue) navigate();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") void checkWhoami();
    };
    let bc: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== "undefined") {
      try {
        bc = new BroadcastChannel("vc-auth");
        bc.addEventListener("message", onBroadcast);
      } catch {
        bc = null;
      }
    }
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisible);
    // Backing-off fallback poll, visible tabs only. This is the last-resort
    // channel for when a service worker or site-isolation policy swallows
    // both (1) and (2) — not a heartbeat. It used to run flat every 5 s
    // forever, which put a steady stream of 401s in the API log from every
    // tab parked on /login and made diagnosing the real session bug
    // (changelog 0511) harder than it needed to be. Doubling from 5 s to a
    // 60 s ceiling keeps the fast path for the common case (a sibling tab
    // picks up the login within seconds) while going quiet on a login page
    // left open all day. Channel (3) still re-checks the moment the
    // operator switches to this tab, so a stale tab is never stuck.
    let delay = 5000;
    let pollId = 0;
    const schedule = () => {
      pollId = window.setTimeout(async () => {
        if (cancelled) return;
        if (document.visibilityState === "visible") await checkWhoami();
        if (cancelled) return;
        delay = Math.min(delay * 2, 60_000);
        schedule();
      }, delay);
    };
    schedule();
    return () => {
      cancelled = true;
      bc?.removeEventListener("message", onBroadcast);
      bc?.close();
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearTimeout(pollId);
    };
  }, [next]);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [codeOpen, setCodeOpen] = useState(false);
  const [codeMode, setCodeMode] = useState<CodeMode>("totp");
  const [totpCode, setTotpCode] = useState("");
  const [backupCode, setBackupCode] = useState("");

  const [pwBusy, setPwBusy] = useState(false);
  const [pkBusy, setPkBusy] = useState(false);
  const [codeBusy, setCodeBusy] = useState(false);
  const [errorKind, setErrorKind] = useState<ErrorKind>(null);
  const [reachable, setReachable] = useState<true | false | null>(null);
  const [secondsToRetry, setSecondsToRetry] = useState(0);

  const checkHealth = useCallback(async () => {
    try {
      // FastAPI exposes /healthz at the root; the catch-all proxy forwards
      // /api/healthz → upstream /healthz.
      const res = await fetch("/api/healthz", { credentials: "include" });
      if (res.ok) {
        setReachable(true);
        setSecondsToRetry(0);
        return;
      }
      setReachable(false);
    } catch {
      setReachable(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let attempt = 0;
    let timer: number | null = null;
    async function loop() {
      if (cancelled) return;
      await checkHealth();
      if (cancelled) return;
      if (reachable === false) {
        attempt += 1;
        const wait = Math.min(30, 2 ** attempt);
        setSecondsToRetry(wait);
        const cd = window.setInterval(() => {
          setSecondsToRetry((s) => (s > 0 ? s - 1 : 0));
        }, 1000);
        timer = window.setTimeout(() => {
          window.clearInterval(cd);
          void loop();
        }, wait * 1000);
      }
    }
    void loop();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reachable === null]);

  async function onPassword(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setPwBusy(true);
    setErrorKind(null);
    try {
      await postJson<{ user_id: string }>("/api/auth/password/login", {
        username: username.trim(),
        password,
      });
      toast.success("Signed in");
      navigateAfterLogin();
    } catch (err) {
      setErrorKind(classify(err, "password"));
    } finally {
      setPwBusy(false);
    }
  }

  async function onPasskey() {
    if (!username.trim()) {
      toast.error("Enter your username first");
      return;
    }
    setPkBusy(true);
    setErrorKind(null);
    try {
      const opts = await postJson<Record<string, unknown>>("/api/auth/login/begin", {
        username: username.trim(),
      });
      const assertion = await startAuthentication({ optionsJSON: opts as never });
      await postJson<{ user_id: string }>("/api/auth/login/complete", {
        credential: assertion,
      });
      toast.success("Signed in");
      navigateAfterLogin();
    } catch (err) {
      setErrorKind(classify(err, "passkey"));
    } finally {
      setPkBusy(false);
    }
  }

  async function onCodeSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!username.trim()) {
      toast.error("Enter your username first");
      return;
    }
    setCodeBusy(true);
    setErrorKind(null);
    try {
      if (codeMode === "totp") {
        if (!/^\d{6}$/.test(totpCode)) {
          setErrorKind("wrong-totp");
          return;
        }
        await postJson<{ user_id: string }>("/api/auth/totp/login", {
          username: username.trim(),
          code: totpCode,
        });
      } else {
        const clean = backupCode.trim().replace(/[\s-]/g, "").toUpperCase();
        if (!clean) return;
        await postJson<{ user_id: string }>("/api/auth/backup-code/login", {
          username: username.trim(),
          code: clean,
        });
      }
      toast.success("Signed in");
      navigateAfterLogin();
    } catch (err) {
      setErrorKind(classify(err, codeMode));
    } finally {
      setCodeBusy(false);
    }
  }

  const errorMeta: ErrorMeta | null = errorKind
    ? {
        title: tErrors(`${ERROR_KIND_TO_KEY[errorKind]}.title`),
        body: tErrors(`${ERROR_KIND_TO_KEY[errorKind]}.body`),
      }
    : null;
  const codeReady =
    codeMode === "totp" ? /^\d{6}$/.test(totpCode) : backupCode.trim().length >= 6;

  return (
    <div style={{ width: "100%", maxWidth: 380 }} data-testid="login-card">
      {reachable === false && (
        <div className="tunnel-banner" role="status" data-testid="tunnel-banner">
          <span className="jp">圏</span>
          <div className="body">
            <div className="ttl">Can't reach server</div>
            <div className="meta">
              retrying in {secondsToRetry}s · check the Cloudflare tunnel or Tailscale
            </div>
          </div>
          <button
            type="button"
            className="retry"
            onClick={() => {
              setReachable(null);
              setSecondsToRetry(0);
            }}
          >
            Retry now
          </button>
        </div>
      )}

      <div className="auth-brand">
        <div className="mark">
          {/* Hanko glyph tracks the active palette so the login mark
             matches whatever theme the user last picked. SSR paints
             the default 朱 before localStorage rehydrates; suppress
             the warning since the swap is intentional and the .mark
             already takes its background from var(--accent), which
             also follows the palette. */}
          <span suppressHydrationWarning>{PALETTE_LABELS[palette].jp}</span>
        </div>
        <div className="name">
          <span className="jp">研</span>vc-engine
        </div>
        <div className="tagline">Your private research vault.</div>
      </div>

      <div className="auth-card">
        {errorMeta && (
          <div className="err-strip" role="alert" data-testid={`login-error-${errorKind}`}>
            <div>
              <b>{errorMeta.title}.</b> {errorMeta.body}
            </div>
            {errorMeta.cta && (
              <a className="cta" href={errorMeta.cta.href}>
                {errorMeta.cta.label}
              </a>
            )}
          </div>
        )}

        {/* Password (primary path) */}
        <form
          onSubmit={onPassword}
          className="auth-form"
          data-testid="login-password-form"
          suppressHydrationWarning
        >
          <label className="auth-label" htmlFor="login-username">
            {tLogin("username")}
          </label>
          <input
            id="login-username"
            className="auth-input"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            data-testid="login-username"
            suppressHydrationWarning
          />
          <label className="auth-label" htmlFor="login-password">
            {tLogin("password")}
          </label>
          <input
            id="login-password"
            className="auth-input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password"
            suppressHydrationWarning
          />
          <button
            type="submit"
            className="submit"
            disabled={pwBusy || !username.trim() || !password}
            data-testid="login-password-submit"
          >
            {pwBusy ? tLogin("signingIn") : tLogin("signIn")}
          </button>
        </form>

        <div className="auth-or">{tLogin("or")}</div>

        {/* Passkey */}
        <button
          type="button"
          className="passkey-btn"
          disabled={pkBusy}
          onClick={onPasskey}
          data-testid="login-passkey"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="ic"
            aria-hidden="true"
          >
            <path d="M12 11c-2.5 0-4 1.5-4 4v3M16 11h-4M16 7H8M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
          </svg>
          <span className="label">
            <span className="top">
              {pkBusy ? "Waiting for passkey…" : "Sign in with passkey"}
            </span>
            <span className="sub">PLATFORM AUTHENTICATOR</span>
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="arr"
            aria-hidden="true"
          >
            <path d="M5 12h14m-6-6 6 6-6 6" />
          </svg>
        </button>

        {/* One-time code toggle */}
        <button
          type="button"
          className="totp-toggle"
          aria-expanded={codeOpen}
          onClick={() => setCodeOpen((o) => !o)}
          data-testid="login-code-toggle"
          style={{ marginTop: 10 }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="ic"
            aria-hidden="true"
          >
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="M6 8h.01M10 8h.01M14 8h.01M18 8h.01M6 12h.01M10 12h.01M14 12h.01M18 12h.01M6 16h12" />
          </svg>
          <span className="grow-label">
            {codeMode === "totp" ? "Use a one-time code" : "Use a backup code"}
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="ic arr"
            aria-hidden="true"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>

        {codeOpen && (
          <form
            onSubmit={onCodeSubmit}
            className="totp-input"
            data-testid="login-code-form"
            suppressHydrationWarning
          >
            {codeMode === "totp" ? (
              <OtpDigits value={totpCode} onChange={setTotpCode} />
            ) : (
              <input
                className="auth-input"
                style={{
                  fontFamily: "var(--font-mono)",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}
                placeholder="B2ECFB06C6"
                autoComplete="off"
                value={backupCode}
                onChange={(e) => setBackupCode(e.target.value)}
                data-testid="login-backup-input"
                suppressHydrationWarning
              />
            )}
            <div className="row">
              <span>
                {codeMode === "totp"
                  ? "6-digit · refreshes every 30s"
                  : "one of the 10-char codes from bootstrap"}
              </span>
              <button
                type="button"
                className="backup"
                onClick={() => {
                  setCodeMode((m) => (m === "totp" ? "backup" : "totp"));
                  setTotpCode("");
                  setBackupCode("");
                }}
              >
                {codeMode === "totp" ? "use backup code →" : "use TOTP →"}
              </button>
            </div>
            <button
              type="submit"
              className="submit"
              disabled={codeBusy || !codeReady || !username.trim()}
              data-testid="login-code-submit"
            >
              {codeBusy ? "Verifying…" : "Sign in"}
            </button>
          </form>
        )}

        <div className="boot-cta">
          First time on this vault?{" "}
          <a href="/bootstrap" className="link">
            Bootstrap →
          </a>
        </div>
      </div>

      <div className="auth-foot">
        vc-engine ·{" "}
        {reachable === true ? (
          <span className="ok">●</span>
        ) : reachable === false ? (
          <span className="err">●</span>
        ) : (
          <span className="muted">●</span>
        )}{" "}
        single-tenant
      </div>
    </div>
  );
}

export default LoginCard;
